import { Dimmer, Form, Grid, Loader } from '@mairistem/composants';
import { isNewIdentifiant, newIdentifiant, request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { deleteDocument, getDocuments } from '@/routes';
import type { ApiResponseType, DocumentType, NewlyAddedFileType } from '@/types';
import RegiePieceJointeList from '../../RegiePieceJointeList/RegiePieceJointeList';

type PiecesJointesProps = {
  identifiant?: string | number;
  addedDocuments?: NewlyAddedFileType[];
  refresh?: boolean;
  onChange?: (e: any, shouldChangeStatus?: boolean) => void;
};

const PiecesJointes = (props: PiecesJointesProps) => {
  const { identifiant, addedDocuments, refresh, onChange } = props;

  const [loading, setLoading] = React.useState(true);
  /** Documents existant en BDD pour une régie déjà créée. */
  const [documentsExistant, setDocumentsExistant] = React.useState<DocumentType[]>([]);

  const refreshRef = React.useRef<boolean>(refresh);

  /** Récupère les documents de la régie */
  const getRegieDocuments = React.useCallback(() => {
    if (!_.isNil(identifiant)) {
      if (isNewIdentifiant(identifiant)) {
        setLoading(false);
        setDocumentsExistant([]);
      } else {
        setLoading(true);

        // on rajoute un faux délai pour laisser le temps
        // à l'upload de se faire.
        // todo: Pas top mais pour l'instant c'est mieux que rien
        setTimeout(() => {
          request
            .get(getDocuments('regie'))
            .then(({ data }: ApiResponseType<DocumentType>) => {
              setDocumentsExistant(data);
            })
            .finally(() => {
              setLoading(false);
            });
        }, 1500);
      }
    }
  }, [identifiant]);

  const handleChangeCollection = React.useCallback(
    (data) => {
      if (onChange) {
        onChange({
          identifiant,
          addedDocuments: _.compact([
            ...(addedDocuments || []),
            { ...data, identifiant: newIdentifiant() },
          ]),
        });
      }
    },
    [addedDocuments, identifiant, onChange],
  );

  const handleDelete = React.useCallback(
    (document) => {
      if (isNewIdentifiant(document.identifiant)) {
        onChange({
          identifiant,
          addedDocuments: _.filter(
            addedDocuments,
            (doc) => doc.identifiant !== document.identifiant,
          ),
        });
      } else {
        // delete bdd
        request.delete(deleteDocument(document.identifiant)).then(() => {
          setDocumentsExistant((prev) => _.reject(prev, { identifiant: document.identifiant }));
        });
      }
    },
    [addedDocuments, identifiant, onChange],
  );

  // Appeler la fonction getRegieDocuments pour récupérer les documents de la régie une seule fois
  React.useEffect(() => {
    getRegieDocuments();
  }, [getRegieDocuments]);

  // Refresh des docs quand on met à jour une fiche
  React.useEffect(() => {
    // Si on était en update et qu'on ne l'est plus
    // = annulation ou validation de la fiche
    if (refreshRef.current && !refresh) {
      getRegieDocuments();
    }

    refreshRef.current = refresh;
  }, [getRegieDocuments, refresh]);

  if (loading) {
    return (
      <Grid as={Form} stackable style={{ height: 100 }}>
        <Grid.Row>
          <Grid.Column>
            <Dimmer active={loading} inverted>
              <Loader inverted content="Chargement de la liste des documents" />
            </Dimmer>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }

  return (
    <Grid as={Form} stackable>
      <Grid.Row>
        <Grid.Column>
          <RegiePieceJointeList
            documentsExistant={documentsExistant}
            addedDocuments={addedDocuments}
            onChange={handleChangeCollection}
            onDelete={handleDelete}
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default PiecesJointes;
